import {
  addDays,
  compareAsc,
  format,
  isAfter,
  isBefore,
  isEqual,
  isToday,
  isWeekend,
  parseISO,
  startOfDay,
} from 'date-fns';
import { ExitSlipAllClassExitSlipQuery$data } from '../graphQLOperations/__generated__/ExitSlipAllClassExitSlipQuery.graphql';
import { ClassExitSlip, ExitSlipClass, LessonData } from '../custom-hooks/useExitSlipChartValues';
import { UserClass } from '../components/TeacherDashboard/ExitSlip/ExitSlipSnapshotCardBody';
import { ExitSlipCurriculumLessonsQuery$data } from '../graphQLOperations/__generated__/ExitSlipCurriculumLessonsQuery.graphql';
import { ExitSlipClassExitSlipsQuery$data } from '../graphQLOperations/__generated__/ExitSlipClassExitSlipsQuery.graphql';
import { splitId } from './splitIdsFromBackEnd';
import { sortByLabelWithDot } from './sortCriteria';
import { formatDateWithTimezone } from './dateFormatting';
import { Nullable } from 'primereact/ts-helpers';

//dueOn is a DateTime, so its calendar day is UTC-anchored (backend classification and ISR both
//read it as UTC). Keying a calendar off the raw string silently never matches — it carries a
//time component — so always go through this.
export const exitSlipDueOnKey = (dueOn: string) => formatDateWithTimezone(dueOn, 'UTC', 'yyyy-MM-dd');

//...then materialize a local Date for day math. PrimeReact's disabledDates compares with local
//getFullYear/getMonth/getDate, so a bare parseISO of the timestamp lands on the previous day in
//any negative-offset timezone and disables the wrong cell.
export const dueOnDay = (dueOn: string) => parseISO(exitSlipDueOnKey(dueOn));

type ExitSlipCurriculumParsed = {
  id: string;
  number: string;
  name: string;
  unit: string;
  targetId: string;
  activityId: string | undefined;
  classExitSlips?: {
    dueOn: string;
    canceledOn?: string | null;
    id: string;
    class: {
      id: string;
      name: string;
    };
  };
};

export const getUpcomingExitSlip = (
  nextLesson: ExitSlipAllClassExitSlipQuery$data,
  today: Date = new Date(),
): LessonData | null => {
  //a canceled ticket is not coming up, however future its due date still reads
  const upcoming = nextLesson.classExitSlips.filter(item => !item.canceledOn && isAfter(dueOnDay(item.dueOn), today));
  const sortedUpcoming = upcoming.sort((a, b) => compareAsc(dueOnDay(a.dueOn), dueOnDay(b.dueOn)));
  if (sortedUpcoming[0]) {
    const lesson = sortedUpcoming[0];
    return {
      id: lesson.id,
      dueOn: lesson.dueOn,
      curriculumLesson: {
        name: lesson.curriculumLesson?.name ?? '',
        number: lesson.curriculumLesson?.number ?? '',
      },
    };
  } else {
    return null;
  }
};

export const getUnfinishedStudents = (exitSlipValues: ClassExitSlip, dateExitSlip: Date | string): UserClass[] => {
  if (typeof dateExitSlip === 'string') dateExitSlip = new Date(dateExitSlip);

  const dateFromCalendar = dateExitSlip.setHours(23, 59, 59, 999) ?? new Date().setHours(23, 59, 59, 999);
  const studentsInClass = exitSlipValues.class
    .map(c => c.userClasses)
    .flat()
    .filter(el => el.user.roles[0].name === 'student')
    .filter(el => new Date(el.createdOn).getTime() <= dateFromCalendar)
    .map(el => el.user);
  const studentDoesMakeES = exitSlipValues.learnosityExitSlipSessions
    .map(s => s.publicUserId)
    .filter((id): id is string => !!id);
  const studentInClassDoesntMakeES = studentsInClass
    .filter(el => !studentDoesMakeES.includes(el?.publicUserId as string))
    .sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? ''));
  return studentInClassDoesntMakeES;
};

export const getAvgQuestions = (exitSlipValues: ClassExitSlip): { avg: number; total: number } | null => {
  const totalOfQuestions = exitSlipValues.learnosityExitSlipSessions[0]?.items?.length ?? null;
  const totalOfSessions = exitSlipValues.learnosityExitSlipSessions.length;
  let sum = 0;
  exitSlipValues.learnosityExitSlipSessions.forEach(element => {
    element.items.forEach(el => {
      if (el.attempted && el.score === el.maxScore) sum++;
    });
  });

  if (totalOfQuestions) {
    return { avg: sum / (totalOfQuestions * totalOfSessions), total: totalOfQuestions };
  } else {
    return null;
  }
};

//targets.name is zero-padded in the DB ("01"), so the row chip needs the padding stripped
export const unitLabel = (targetName: string | null | undefined): string => {
  if (!targetName) return '';
  const parsed = parseInt(targetName, 10);
  return Number.isNaN(parsed) ? `Unit ${targetName}` : `Unit ${parsed}`;
};

//"Lesson 1.2: Round Numbers" -> "1.2". The colon is optional so a day claim built from a
//bare lesson number ("Lesson 1.2") tags its calendar cell too.
export const lessonTag = (title: string) => title.match(/^Lesson\s+([^:]+?)\s*(?::|$)/)?.[1] ?? '';

//shapes shared with the reschedule flow (TEAMB-3422) so both surfaces drive one calendar
export interface ExitSlipCascadeMove {
  index: number;
  from: Date;
  to: Date;
}

//a row's position in the upcoming array is the index the planners and views share
export interface ExitSlipRescheduleRow {
  date: Date;
  classExitSlipId: string;
  curriculumLessonId: string;
  title: string;
}

export type ExitSlipPastTag = Pick<ExitSlipRescheduleRow, 'date' | 'title'>;

/**
 * Days locked by tickets the panel cannot edit — other units, other roadmaps, or lessons
 * outside the current query. Rows the panel *can* edit are deliberately excluded: their days
 * come from the live staged list instead, so moving a row frees the day it left behind
 * instead of blocking it until the next refetch.
 */
export const getSavedDayClaims = <
  T extends {
    readonly dueOn: string;
    readonly canceledOn?: string | null;
    readonly curriculumLesson?: { readonly id: string } | null;
  },
>(
  slips: readonly T[],
  editableLessonIds: string[],
  titleFor: (slip: T) => string,
): ExitSlipPastTag[] => {
  const editable = new Set(editableLessonIds);
  return (
    slips
      //TEAMB-3483 a canceled ticket releases its day. The DB's unique key skips canceled rows, so a
      //replacement may be assigned onto that date and the picker has to offer it rather than grey it
      //out under a lesson that is no longer happening.
      .filter(slip => !slip.canceledOn)
      .filter(slip => !slip.curriculumLesson?.id || !editable.has(slip.curriculumLesson.id))
      .map(slip => ({ date: startOfDay(parseISO(slip.dueOn)), title: titleFor(slip) }))
  );
};

//the days the panel's own rows currently hold, saved or not
export const getStagedDayClaims = (
  rows: { id: string; title: string }[],
  lessonDates: { curriculumLessonId: string; date: Date | null }[],
): ExitSlipPastTag[] => {
  const titles = new Map(rows.map(row => [row.id, row.title]));
  return lessonDates
    .filter(el => !!el.date && titles.has(el.curriculumLessonId))
    .map(el => ({ date: startOfDay(el.date as Date), title: titles.get(el.curriculumLessonId) as string }));
};

export const getDayClaims = <
  T extends {
    readonly dueOn: string;
    readonly canceledOn?: string | null;
    readonly curriculumLesson?: { readonly id: string; readonly number?: string | null } | null;
  },
>(
  slips: readonly T[],
  editableLessonIds: string[],
  today: Date = startOfDay(new Date()),
) => {
  const title = (slip: T) => `Lesson ${slip.curriculumLesson?.number ?? ''}`;
  const isPast = (slip: T) => isBefore(startOfDay(parseISO(slip.dueOn)), today);
  return {
    savedClaims: getSavedDayClaims(
      slips.filter(slip => !isPast(slip)),
      editableLessonIds,
      title,
    ),
    pastTags: getSavedDayClaims(slips.filter(isPast), [], title),
  };
};

/**
 * Which days the row's date picker must refuse.
 *
 * A class can hold at most one exit ticket per day — the DB enforces it with a unique
 * constraint on (class, due_on), so anything this misses comes back as a save error
 * rather than a blocked cell.
 *
 * Three sources feed in:
 *  - `saved`  — tickets already persisted for the selected class(es).
 *  - `staged` — dates picked in other rows during this session, not yet saved.
 *  - `ownDate` — the row's own current date. It appears in `saved`/`staged`, but the row
 *    must not block itself or reopening the picker shows the current date greyed out.
 */
export const getBlockedDays = (
  saved: ExitSlipPastTag[],
  staged: ExitSlipPastTag[],
  ownDate: Date | null,
): ExitSlipPastTag[] => {
  const own = ownDate ? startOfDay(ownDate).getTime() : null;
  const byDay = new Map<number, ExitSlipPastTag>();

  //staged last so an unsaved move's title wins over the saved one it is replacing
  [...saved, ...staged].forEach(claim => {
    const day = startOfDay(claim.date).getTime();
    if (day === own) return;
    byDay.set(day, { date: startOfDay(claim.date), title: claim.title });
  });

  return [...byDay.values()];
};

export const shortDay = (date: Date) => format(date, 'EEE, MMM d');

//the panel shows today's ticket in its own section above the upcoming list
export const splitTodayUpcoming = <T extends { date?: Date | null; canceledDate?: Date | null }>(lessons: T[]) => {
  const isDueToday = (el: T) => {
    const day = el.date ?? el.canceledDate;
    return !!day && isToday(day);
  };

  return {
    todayLessons: lessons.filter(isDueToday),
    upcomingLessons: lessons.filter(el => !isDueToday(el)),
  };
};

export const getPassedUpcomingLessons = (data: ExitSlipCurriculumLessonsQuery$data, classesIds: string[]) => {
  const filteredData = {
    curriculumLessons: data.curriculumLessons
      .filter(lesson => lesson.classExitSlips?.some(exitSlip => classesIds.includes(exitSlip?.class?.id as string)))
      .map(lesson => ({
        ...lesson,
        classExitSlips: lesson?.classExitSlips?.filter(exitSlip => classesIds.includes(exitSlip?.class?.id as string)),
      })),
  };

  const now = new Date();
  const todayInZone = startOfDay(now);
  const passedLessons: ExitSlipCurriculumParsed[] = [];
  const upcomingLessons: ExitSlipCurriculumParsed[] = [];

  filteredData.curriculumLessons.forEach(lesson => {
    if (!lesson?.classExitSlips) return;

    lesson.classExitSlips
      .filter(el => el?.class?.id && classesIds.includes(el.class.id))
      .forEach(el => {
        const dueDate = dueOnDay(el?.dueOn ?? '');
        const lessonData = {
          id: lesson.id,
          number: lesson.number as string,
          name: lesson.name as string,
          unit: unitLabel(lesson.target?.name),
          targetId: lesson.target?.id ?? '',
          activityId: lesson?.curriculumLessonTargetSkills?.[0]?.targetSkill?.skill?.code
            ? `ExitSlip_Miami_${splitId(lesson?.id as string)}_${lesson?.curriculumLessonTargetSkills?.[0]?.targetSkill
                ?.skill?.code}`
            : undefined,
          classExitSlips: {
            dueOn: el?.dueOn,
            canceledOn: el?.canceledOn,
            id: el?.id as string,
            class: {
              id: el?.class?.id as string,
              name: el?.class?.name as string,
            },
          },
        };

        //TEAMB-3566 a canceled ticket stays on its due day's tab; the row offers Re-assign in either
        if (isBefore(dueDate, todayInZone)) {
          passedLessons.push(lessonData);
        } else if (isAfter(dueDate, todayInZone) || isEqual(dueDate, todayInZone)) {
          upcomingLessons.push(lessonData);
        }
      });
  });

  const byLessonNumber = (a: ExitSlipCurriculumParsed, b: ExitSlipCurriculumParsed) =>
    sortByLabelWithDot({ label: a.number }, { label: b.number });

  return {
    passedLessons: passedLessons.sort(byLessonNumber),
    upcomingLessons: upcomingLessons.sort(byLessonNumber),
  };
};

export const groupByCurriculumLesson = (data: ExitSlipClassExitSlipsQuery$data): ClassExitSlip[] => {
  //TEAMB-3483 a canceled ticket stays on the dashboard so the day's responses remain reviewable,
  //but only until the teacher assigns a replacement for that day — then the live ticket takes the
  //card and the canceled one is reachable from the side panel's Past tab instead.
  //
  //Per class, not across the whole result: the query covers one day but every selected class, so
  //one class's replacement must not hide another class's canceled ticket.
  const replacedClassIds = new Set(data.classExitSlips.filter(item => !item?.canceledOn).map(item => item?.class?.id));
  const slips = data.classExitSlips.filter(item => !item?.canceledOn || !replacedClassIds.has(item?.class?.id));

  const grouped = slips.reduce(
    (acc, item) => {
      const lessonKey =
        item?.curriculumLesson?.id || `${item?.curriculumLesson?.number}-${item?.curriculumLesson?.name}`;

      if (!acc[lessonKey]) {
        acc[lessonKey] = {
          id: item?.id ?? '',
          canceledOn: item?.canceledOn,
          curriculumLesson: item?.curriculumLesson
            ? {
                id: item.curriculumLesson.id ?? '',
                number: item.curriculumLesson.number ?? '',
                name: item.curriculumLesson.name ?? '',
                targetGroup: { name: item.curriculumLesson.targetGroup?.name ?? '' },
                skill: Array.isArray(item.curriculumLesson.skills)
                  ? {
                      code: item.curriculumLesson.skills[0]?.code ?? '',
                      id: item.curriculumLesson.skills[0]?.id ?? '',
                      name: item.curriculumLesson.skills[0]?.name ?? '',
                    }
                  : {
                      code: '',
                      id: '',
                      name: '',
                    },
              }
            : {
                id: '',
                number: '',
                name: '',
                targetGroup: { name: '' },
                skill: {
                  code: '',
                  id: '',
                  name: '',
                },
              },
          learnosityExitSlipSessions: [],
          class: [],
        };
      }

      if (Array.isArray(item.learnosityExitSlipSessions)) {
        acc[lessonKey].learnosityExitSlipSessions.push(...item.learnosityExitSlipSessions);
      }

      if (item.class && Array.isArray(item.class.userClasses)) {
        acc[lessonKey].class.push(item.class as ExitSlipClass);
      }

      return acc;
    },
    {} as Record<string, ClassExitSlip>,
  );
  return Object.values(grouped);
};

export const getFullDetailModalData = (
  exitSlipValues: ClassExitSlip,
): { usersIds: { id: string; name: string }[]; sessionIds: string[] } => {
  const usersIds = exitSlipValues.learnosityExitSlipSessions
    .flatMap(el => ({
      id: el.user.publicUserId as string,
      name: el.user.fullName as string,
      lastName: el.user.lastName ?? '',
    }))
    .sort((a, b) => a.lastName.localeCompare(b.lastName));

  const sessionIds = exitSlipValues.learnosityExitSlipSessions.map(el => el.sessionId as string);

  return { usersIds, sessionIds };
};

const dayId = (date: Date) => startOfDay(date).getTime();

const nextClassDay = (date: Date, blocked?: Set<number>): Date => {
  let next = addDays(date, 1);
  while (isWeekend(next) || blocked?.has(dayId(next))) next = addDays(next, 1);
  return next;
};

//TEAMB-3379 one exit ticket per class per day: landing on a taken day pushes the sitting
//ticket to the next free class day and whatever it lands on chains onward. This is the
//default strategy; the opt-in 'replace' alternative plans no moves at all, because it
//unassigns the sitting ticket instead of rehoming it.
//A move's index is the mover's position in the dates array.
export const planExitSlipCascade = (
  target: Nullable<Date>,
  movingIndex: number,
  dates: Date[],
  blockedDays: Date[] = [],
): ExitSlipCascadeMove[] => {
  if (!target) return [];

  const blocked = new Set(blockedDays.map(dayId));
  const occupants = new Map<number, { index: number; date: Date }>();
  dates.forEach((date, index) => {
    if (index !== movingIndex) occupants.set(dayId(date), { index, date });
  });

  const moves: ExitSlipCascadeMove[] = [];
  let day = startOfDay(target);
  let sitting = occupants.get(day.getTime());

  //every hop consumes one occupant, so the chain always terminates
  while (sitting) {
    const to = nextClassDay(day, blocked);
    moves.push({ index: sitting.index, from: sitting.date, to });
    occupants.delete(day.getTime());
    day = to;
    sitting = occupants.get(to.getTime());
  }

  return moves;
};

//TEAMB-3485 the (class, due_on) unique constraint is PER CLASS, so a schedule mixing classes
//would cascade one class's tickets onto another's days. Scope to one class to reschedule.
export const getExitSlipSchedule = (
  data: ExitSlipAllClassExitSlipQuery$data,
  classId?: string,
  today: Date = new Date(),
) => {
  const from = startOfDay(today);
  const upcoming: ExitSlipRescheduleRow[] = [];
  const past: ExitSlipPastTag[] = [];
  //upsert inputs require a curriculumLessonId, so a slip without one can't be moved — but its
  //day still holds a row under the (class, due_on) unique constraint and must stay occupied
  const blocked: Date[] = [];
  //marked, never occupied: see the canceled branch below
  const canceled: Date[] = [];

  data.classExitSlips.forEach(el => {
    const lesson = el.curriculumLesson;
    if (!el.dueOn) return;
    if (classId && el.class?.id !== classId) return;

    const date = dueOnDay(el.dueOn as string);
    const title = `Lesson ${lesson?.number ?? ''}: ${lesson?.name ?? ''}`;

    //TEAMB-3483 a canceled ticket releases its day: the DB's unique key skips canceled rows, so
    //the teacher can assign a replacement onto that date and the calendar must offer it. It is
    //collected separately rather than dropped so the day can still be marked — but it must never
    //reach past/upcoming/blocked, or it would tag the cell with a lesson that is no longer
    //happening and make a free day look taken.
    if (el.canceledOn) {
      canceled.push(date);
      return;
    }

    if (isBefore(date, from)) past.push({ date, title });
    else if (lesson?.id) upcoming.push({ date, title, classExitSlipId: el.id, curriculumLessonId: lesson.id });
    else blocked.push(date);
  });

  return { past, blocked, canceled, upcoming: upcoming.sort((a, b) => compareAsc(a.date, b.date)) };
};

export type ExitSlipMoveStrategy = 'push' | 'replace';

export const lessonName = (title: string) => {
  const tag = lessonTag(title);
  return tag ? `Lesson ${tag}` : title.split(':')[0];
};

//the toast reads as a bold outcome line over a lighter consequence line, so the copy is built as a
//pair rather than one em-dashed string
export interface ExitSlipToastMessage {
  header: string;
  detail?: string;
}

export const describeExitSlipMove = (
  moverTitle: string,
  target: Date,
  moves: ExitSlipCascadeMove[],
  replacedTitle: string,
): ExitSlipToastMessage => {
  const header = `${lessonName(moverTitle)} is now scheduled for ${shortDay(target)}`;
  if (replacedTitle) return { header, detail: `${lessonName(replacedTitle)} was removed from the schedule` };
  if (!moves.length) return { header };

  return { header, detail: `${moves.length} exit ticket${moves.length > 1 ? 's' : ''} moved forward` };
};

export const describeExitSlipCancel = (title: string, responseCount: number): ExitSlipToastMessage => ({
  header: `${lessonName(title)} canceled successfully`,
  detail: responseCount
    ? `The ${responseCount} response${responseCount > 1 ? 's' : ''} already in ${responseCount > 1 ? 'are' : 'is'} kept`
    : `It's back in Unassigned exit tickets`,
});

//apply if original date its and old date and the new date its a future date
export const applyExitSlipReassignment = (
  originalDate: Date | null,
  newDate: Nullable<Date>,
  today: Date = new Date(),
) => (originalDate && newDate ? originalDate < startOfDay(today) && newDate > today : false);
